import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { formatDateOnly, errorWords } from "@alm/domain";
import { Eye, FileText, Pencil, Trash2, Upload } from "@/lib/icons";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { toast } from "sonner";
import { useFileViewer } from "@/components/app/sending/file-viewer";
import { OverflowText } from "@/components/app/lists/overflow-text";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { RowActions } from "@/components/app/patterns/row-actions";
import { TableCard } from "@/components/app/patterns/table-card";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { useZone } from "@/lib/facility/zone";
import { DocumentRenameDialog } from "./-components/documents/document-rename-dialog";
import { documentSize, DocumentUploadDialog } from "./-components/documents/document-upload-dialog";
import { SectionEmpty } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/section-empty";
import { RequestSignatureDialog } from "./-components/portal/request-signature-dialog";
import { SigningHistoryCard } from "./-components/portal/signing-history-card";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/documents")({
  component: DocumentsTab,
});

type DocumentRow = Output<typeof orpcClient.care.residents.documents.list>[number];

function documentColumns(zone: string): ColumnDef<DocumentRow>[] {
  return [
    {
      accessorKey: "customName",
      header: "Document name",
      enableHiding: false,
      cell: ({ row }) => (
        <>
          <OverflowText lines={1} className="font-medium">
            {row.original.customName}
          </OverflowText>
          {row.original.noteContent ? (
            <OverflowText lines={1} className="text-muted-foreground mt-0.5 text-xs">
              {row.original.noteContent}
            </OverflowText>
          ) : null}
        </>
      ),
    },
    {
      id: "type",
      accessorFn: (row) =>
        row.source === "note"
          ? `From note${row.documentTypeName ? `, ${row.documentTypeName}` : ""}`
          : row.documentTypeName || "Unknown",
      header: "Document type",
      meta: { filter: "select" },
      cell: ({ row, getValue }) => (
        <span className={row.original.source === "note" ? "text-muted-foreground" : undefined}>
          {String(getValue())}
        </span>
      ),
    },
    {
      id: "uploaded",
      accessorFn: (row) => (row.uploadedAt ? new Date(row.uploadedAt).getTime() : 0),
      header: "Uploaded date",
      meta: { nowrap: true, className: "tabular-nums" },
      cell: ({ row }) =>
        row.original.uploadedAt ? (
          formatDateOnly(row.original.uploadedAt, zone)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "uploadedBy",
      accessorFn: (row) => row.uploadedByName || "Unknown",
      header: "Uploaded by",
      meta: { priority: 2, filter: "select" },
      cell: ({ getValue }) => <PersonCell size="sm" name={String(getValue())} />,
    },
    {
      id: "size",
      accessorFn: (row) => row.fileSize ?? 0,
      header: "File size",
      meta: {
        align: "right",
        nowrap: true,
        priority: 3,
        className: "text-muted-foreground tabular-nums",
      },
      cell: ({ row }) => documentSize(row.original.fileSize),
    },
  ];
}

/** Every file on the chart, and the signature sessions behind the signed ones. */
function DocumentsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("resident.documents:update", resident.facilityId);
  const zone = useZone(resident.facilityId);
  const queryClient = useQueryClient();
  const { openFile, fileViewer } = useFileViewer();
  const [dialog, setDialog] = useState<"upload" | "signature" | null>(null);
  const [renaming, setRenaming] = useState<DocumentRow | null>(null);
  const [deleting, setDeleting] = useState<DocumentRow | null>(null);
  const documents = useQuery(
    orpc.care.residents.documents.list.queryOptions({ input: { residentId } }),
  );

  const remove = useMutation(
    orpc.care.residents.documents.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Document deleted");
        setDeleting(null);
        await queryClient.invalidateQueries({ queryKey: orpc.care.residents.documents.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const view = async (row: DocumentRow) => {
    try {
      const result =
        row.source === "note"
          ? await orpcClient.care.notes.attachments.downloadUrl({ attachmentId: row.id })
          : row.source === "authorization"
            ? await orpcClient.care.residents.authorizations.documents.downloadUrl({
                authorizationDocumentId: row.id,
              })
            : await orpcClient.care.residents.documents.downloadUrl({ documentId: row.id });
      openFile({ url: result.url, fileName: row.customName, contentType: row.mimeType });
    } catch (error) {
      toast.error(errorWords(error));
    }
  };
  const rows = documents.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      {fileViewer}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Documents</h2>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {rows.length > 0 ? `${rows.length} on file` : "Signed forms, uploads and scans"}
          </p>
        </div>
        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDialog("signature")}>
              <FileText className="size-4" />
              Request signature
            </Button>
            <Button onClick={() => setDialog("upload")}>
              <Upload className="size-4" />
              Upload document
            </Button>
          </div>
        ) : null}
      </div>

      {documents.isError ? (
        <TableCard>
          <ReadFailed
            className="p-4"
            message="Could not load this resident's documents."
            error={documents.error}
            onRetry={() => void documents.refetch()}
            retrying={documents.isFetching}
          />
        </TableCard>
      ) : (
        <DataTable
          pending={documents.isPending}
          data={rows}
          columns={documentColumns(zone)}
          noun="documents"
          rowId={(row) => `${row.source}-${row.id}`}
          empty={
            <SectionEmpty
              title="No documents uploaded"
              detail="Upload a document, or request a signature to start a signing session."
            />
          }
          renderToolbar={(table) =>
            rows.length > 0 ? (
              <DataTableToolbar table={table} searchPlaceholder="Search documents…" />
            ) : null
          }
          renderRowActions={(row) => (
            <RowActions
              label={row.customName}
              modal={false}
              primary={{ label: "View", icon: Eye, onSelect: () => void view(row) }}
              items={
                canWrite && row.source === "documents"
                  ? [
                      { label: "Rename", icon: Pencil, onSelect: () => setRenaming(row) },
                      {
                        label: "Delete",
                        icon: Trash2,
                        destructive: true,
                        onSelect: () => setDeleting(row),
                      },
                    ]
                  : []
              }
            />
          )}
        />
      )}

      <SigningHistoryCard residentId={residentId} canWrite={canWrite} />

      {dialog === "upload" ? (
        <DocumentUploadDialog
          owner={{ kind: "resident", id: residentId }}
          facilityId={resident.facilityId}
          onClose={() => setDialog(null)}
        />
      ) : null}
      <RequestSignatureDialog
        residentId={residentId}
        facilityId={resident.facilityId}
        open={dialog === "signature"}
        onOpenChange={(open) => setDialog(open ? "signature" : null)}
        onCreated={() =>
          queryClient.invalidateQueries({ queryKey: orpc.administration.signing.key() })
        }
      />
      {renaming ? (
        <DocumentRenameDialog
          owner="resident"
          document={{
            id: renaming.id,
            name: renaming.customName,
            typeId: renaming.documentTypeId ?? "",
          }}
          onClose={() => setRenaming(null)}
        />
      ) : null}
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? null : setDeleting(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleting?.customName}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white"
              disabled={remove.isPending}
              onClick={() => (deleting ? remove.mutate({ documentId: deleting.id }) : null)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
